const BOAT_RANK = { wood: 1, iron: 2, speed: 3 };
const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const validQuantity = (value) => Number.isSafeInteger(value) && value > 0;
const validDay = (value) => Number.isSafeInteger(value) && value > 0;
const held = (bag, id) => validQuantity(bag?.[id]) ? bag[id] : 0;
const currentDay = (state) => validDay(state.dayId) ? state.dayId : 1;
const offerCount = (state) => state.shopLevel >= 3 ? 2 : 1;
const validatedBoards = new WeakMap();

function fixedDefinition(order) {
  const requirements = Object.freeze(order.requirements.map((row) => Object.freeze({ ...row })));
  // Completion remains mutable; the validated recipe and reward cannot change in place.
  for (const [key, value] of Object.entries({ id: order.id, requirements, bonus: 10 })) {
    Object.defineProperty(order, key, { value, enumerable: true, writable: false, configurable: false });
  }
  return order;
}

function seafoodPool(state, seafood) {
  return Object.keys(seafood).filter((id) => {
    const spec = seafood[id];
    return (!spec.rodLevel || state.rodLevel >= spec.rodLevel)
      && (!spec.diveLevel || state.diveLevel >= spec.diveLevel)
      && (!spec.boatTier || (BOAT_RANK[state.boatTier] || 0) >= BOAT_RANK[spec.boatTier]);
  });
}

function cropPool(state, crops) {
  return Object.keys(crops).filter((id) => held(state.inventory, id) || held(state.storage, id)
    || held(state.inventory, `${id}Seed`) || held(state.storage, `${id}Seed`)
    || state.plots?.some((plot) => plot?.crop?.id === id)
    || validQuantity(state.islandLife?.journal?.[id]?.count));
}

function makeOrder(state, dayId, slot, { crops, seafood }) {
  const order = { id: `${dayId}-${slot + 1}`, requirements: [], bonus: 10, completed: false };
  const cropIds = cropPool(state, crops);
  if (state.shopLevel < 2 || !cropIds.length) {
    order.requirements = [{ itemId: state.shopLevel < 2 && dayId % 2 === 0 ? 'sweetPotato' : 'crab', quantity: 1 }];
    return order;
  }
  const seafoodIds = seafoodPool(state, seafood);
  order.requirements = [
    { itemId: seafoodIds[(dayId - 1 + slot) % seafoodIds.length] || 'crab', quantity: (dayId + slot) % 3 + 1 },
    { itemId: cropIds[(dayId - 1 + slot) % cropIds.length], quantity: (dayId + slot + 1) % 3 + 1 },
  ];
  return order;
}

function cleanRequirements(value, { crops, seafood }) {
  if (!Array.isArray(value) || ![1, 2].includes(value.length)) return null;
  const seen = new Set();
  const rows = [];
  for (const row of value) {
    if (!row || typeof row !== 'object' || typeof row.itemId !== 'string' || seen.has(row.itemId)
      || !validQuantity(row.quantity) || row.quantity > 3 || (!has(crops, row.itemId) && !has(seafood, row.itemId))) return null;
    seen.add(row.itemId);
    rows.push({ itemId: row.itemId, quantity: row.quantity });
  }
  if (rows.length === 1) {
    return ['crab', 'sweetPotato'].includes(rows[0].itemId) && rows[0].quantity === 1 ? rows : null;
  }
  return rows.filter((row) => has(crops, row.itemId)).length === 1
    && rows.filter((row) => has(seafood, row.itemId)).length === 1 ? rows : null;
}

// Saved recipes stay fixed for the game day. Only known items, bounded quantities,
// exact slot IDs and the fixed bonus survive hydration; saved rewards are never trusted.
export function normalizeDailyOrderBoard(raw, state, catalog) {
  const dayId = currentDay(state);
  const count = offerCount(state);
  const sameDay = raw && typeof raw === 'object' && !Array.isArray(raw) && raw.dayId === dayId;
  const frozenDefinitions = sameDay && raw.definitionVersion === 1;
  const previous = sameDay && Array.isArray(raw.offers) ? raw.offers.slice(0, 2) : [];
  const offers = Array.from({ length: count }, (_, slot) => {
    const generated = makeOrder(state, dayId, slot, catalog);
    const saved = previous.find((offer) => offer?.id === generated.id);
    if (!saved) return fixedDefinition(generated);
    return fixedDefinition({
      ...generated,
      requirements: frozenDefinitions && cleanRequirements(saved.requirements, catalog) || generated.requirements,
      completed: saved.completed === true,
    });
  });
  const board = { dayId, definitionVersion: 1, offers: Object.freeze(offers) };
  validatedBoards.set(board, { dayId, count, offers: board.offers });
  return board;
}

export function ensureDailyOrderBoard(state, catalog) {
  const cached = state.orderBoard && validatedBoards.get(state.orderBoard);
  if (cached && cached.dayId === currentDay(state) && state.orderBoard.dayId === cached.dayId
    && cached.count === offerCount(state) && state.orderBoard.offers === cached.offers) return cached.offers;
  state.orderBoard = normalizeDailyOrderBoard(state.orderBoard, state, catalog);
  return state.orderBoard.offers;
}

// This describes an existing offer and never refreshes or mutates the board.
export function readDailyOrderStatus(state, orderId, catalog) {
  const dayId = currentDay(state);
  if (state.orderBoard?.dayId !== dayId || !Array.isArray(state.orderBoard.offers)) return null;
  const index = state.orderBoard.offers.findIndex((offer) => offer?.id === orderId);
  if (index < 0 || index > 1 || orderId !== `${dayId}-${index + 1}`) return null;
  const order = state.orderBoard.offers[index];
  const definition = cleanRequirements(order.requirements, catalog);
  if (!definition) return null;
  const requirements = definition.map(({ itemId, quantity }) => ({
    itemId, label: catalog.crops[itemId]?.label || catalog.seafood[itemId].label,
    quantity, held: held(state.inventory, itemId), stored: held(state.storage, itemId),
  }));
  const completed = order.completed === true;
  const available = !completed && requirements.every((row) => row.held >= row.quantity);
  const baseValue = requirements.reduce((sum, row) => sum + row.quantity
    * (catalog.crops[row.itemId]?.price ?? catalog.seafood[row.itemId].price), 0);
  const bonus = 10;
  return {
    id: order.id, title: completed ? `今日委托 ${index + 1} · 已交付` : `今日委托 ${index + 1}`,
    detail: completed ? '已结清 · 次日清晨 06:00 更新' : available ? '材料已齐 · 到海岛手记牌交付'
      : `${requirements.map((row) => `${row.label} ${Math.min(row.held, row.quantity)}/${row.quantity}`).join(' · ')} · 自愿完成，无未完成惩罚`,
    requirements, completed, available, baseValue, bonus, value: baseValue + bonus,
  };
}
